<script setup>
import { useCalendar } from '@daysmart/daysmart-vue-components/composables'
import { groupData } from '@daysmart/daysmart-vue-components/utils'
import { useCalendarSettings } from '../composables/useCalendarSettings'
import isBetweenDateRange from '~/core/utils/isBetweenDateRange'

const props = defineProps({
  events: {
    type: Object,
  },
  date: String,
  closedDates: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['select-date', 'update-view'])

const { t, d } = useI18n()
const { customFirstDayOfWeek } = useCalendarSettings()
const calendar = useCalendar({ customFirstDayOfWeek })

const monthView = computed(() => calendar.getMonthView.value(props.date))

const weeks = computed(() =>
  groupData({
    data: monthView.value,
    getGroupKey: (day) => {
      const monthIndex = monthView.value.findIndex(
        monthDay => monthDay.isSame(day, 'day'),
      )
      return Math.floor(monthIndex / 7)
    },
  }).sort((a, b) => a.key - b.key),
)

const hasEvents = computed(() => {
  const starts = monthView.value[0].$d
  const ends = monthView.value[monthView.value.length - 1].$d

  return (
    props.events?.filter(event =>
      isBetweenDateRange(event.starts_at, starts, ends),
    ).length > 0
  )
})

function getEventsFromDay(day) {
  const eventsOnTheDay = props.events?.filter(event =>
    day.isSame(event.starts_at, 'day'),
  )

  return eventsOnTheDay.sort((a, b) =>
    new Date(a.starts_at)
      < new Date(b.starts_at)
      ? -1
      : 1,
  )
}

function checkDaysContainerRounding(weekIndex, dayIndex, weeks, weekData) {
  if (weekIndex === 0 && dayIndex === 0)
    return 'first-day-of-month'
  if (weekIndex === 0 && dayIndex + 1 === weekData.length)
    return 'last-day-of-first-week'
  if (weekIndex + 1 === weeks.length && dayIndex === 0)
    return 'first-day-of-last-week'
  if (weekIndex + 1 === weeks.length && dayIndex + 1 === weekData.length)
    return 'last-day-of-month'
}

function selectMobileDate(date) {
  emit('select-date', date)
  emit('update-view', 'day')
}
</script>

<template>
  <div class="monthly-view gap-4 p-4">
    <ScheduleNoEvents
      v-if="!hasEvents"
      :text="t('There are no events scheduled for this month')"
    />
    <div class="desktop-calendar-month-view">
      <DesktopScrollContainer>
        <div
          v-for="(week, weekIndex) in weeks"
          :key="weekIndex"
          class="days-container first:rounded-t-8px last:rounded-b-8px"
        >
          <template
            v-for="(day, dayIndex) in week.data"
            :key="dayIndex"
          >
            <ScheduleEventItemContainer
              class="w-[calc(100%/7-1px)]"
              :class="{
                'rounded-tl-8px':
                  checkDaysContainerRounding(
                    weekIndex,
                    dayIndex,
                    weeks,
                    week.data,
                  ) === 'first-day-of-month',
                'rounded-tr-8px':
                  checkDaysContainerRounding(
                    weekIndex,
                    dayIndex,
                    weeks,
                    week.data,
                  ) === 'last-day-of-first-week',
                'rounded-bl-8px':
                  checkDaysContainerRounding(
                    weekIndex,
                    dayIndex,
                    weeks,
                    week.data,
                  ) === 'first-day-of-last-week',
                'rounded-br-8px':
                  checkDaysContainerRounding(
                    weekIndex,
                    dayIndex,
                    weeks,
                    week.data,
                  ) === 'last-day-of-month',
              }"
              :date="day.format('YYYY-MM-DD')"
              :is-closed="props.closedDates.includes(day.format('YYYY-MM-DD'))"
            >
              <template #caption>
                <strong v-if="day.date() === 1">
                  {{ d(day.format('YYYY-MM-DD'), 'day') }}
                </strong>
                <span v-else>
                  {{ day.date() }}
                </span>
              </template>
              <ScheduleClosedSign v-if="closedDates.includes(day.format('YYYY-MM-DD'))" />
              <template
                v-for="(event, eventIndex) in getEventsFromDay(day)"
                :key="eventIndex"
              >
                <ScheduleEventItem :event="event" />
              </template>
            </ScheduleEventItemContainer>
          </template>
        </div>
      </DesktopScrollContainer>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.desktop-calendar-month-view {
  .days-container {
    @apply flex bg-surface-600 flex-gap-1px p-1px pt-0 first:pt-1px;
  }
}
</style>

<i18n>
{
  "en-us": {
    "There are no events scheduled for this month": "There are no events scheduled for this month"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "There are no events scheduled for this month": "Für diesen Monat sind keine Veranstaltungen geplant"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "There are no events scheduled for this month": "Il n'y a aucun événement programmé pour ce mois"
}
</i18n>

<i18n locale="es" lang="json">
{
  "There are no events scheduled for this month": "No hay eventos programados para este mes"
}
</i18n>
